/**
 * GarminCalendar — month view of the activities Garmin has pushed to us.
 *
 * Why a calendar rather than a list: people find a race by when it happened.
 * And why only a calendar of what we already hold — Garmin Starter Access is
 * push-only with no historical access, so there is nothing to query.
 * Activities appear from the moment the user connects, and never before that.
 *
 * Layout: markers in the grid, detail in a list below the selected day. Chips
 * drawn inside the day cells could not survive a day with three activities on
 * a phone, and the list is also where the per-activity device credit belongs —
 * a header credit is meaningless once someone owns two watches, since it
 * cannot say which activity came from which.
 *
 * Attribution: the Garmin API brand guidelines require a "Garmin [device model]"
 * credit on any view of device-sourced data, above the fold and never hidden in
 * a tooltip. For multi-entry displays they allow it per entry, which is what
 * each row carries.
 *
 * Props
 * -----
 * sport        'run' | 'bike'
 * selectedId   currently selected activity id (from the drawer's selectionMap)
 * onSelect     (id) => void     single click
 * onConfirm    (id) => void     double click — select and act
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Typography, useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import WatchIcon from '@mui/icons-material/Watch';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme';
import { garminDeviceLabel } from './GarminAttribution';
import {
  getGarminAuthUrl, getGarminStatus, listGarminActivities,
} from '../../api/garminApi';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const monthBounds = (year, month) => ({
  start: iso(new Date(year, month, 1)),
  end: iso(new Date(year, month + 1, 1)),
});

/** Monday-first grid of 6 weeks covering the month. */
const buildWeeks = (year, month) => {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7; // Sunday=0 -> Monday-first
  const cursor = new Date(year, month, 1 - offset);
  const weeks = [];
  for (let w = 0; w < 6; w += 1) {
    const days = [];
    for (let d = 0; d < 7; d += 1) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(days);
  }
  return weeks;
};

const fmtDistance = (m) => (m == null ? null : `${(m / 1000).toFixed(m < 10000 ? 2 : 1)} km`);

const fmtDuration = (s) => {
  if (s == null) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`;
};

/**
 * Why an activity cannot be imported, in the user's terms.
 *
 * This used to be one hardcoded "the file was never received", which lied
 * whenever the file HAD arrived and the download was the thing that failed.
 * Each state has a different cause and a different answer, so each says so.
 */
const unavailableReason = (status, t) => {
  switch (status) {
    case 'pending':
    case 'downloading':
      return {
        short: t('garminFilePending', 'Fetching file'),
        long: t('garminFilePendingLong',
          'The file is being fetched from Garmin. Check back in a minute.'),
      };
    case 'failed':
      return {
        short: t('garminFileFailed', 'Fetch failed'),
        long: t('garminFileFailedLong',
          'The file could not be fetched from Garmin after several attempts.'),
      };
    case 'expired':
      return {
        short: t('garminFileExpired', 'No longer available'),
        long: t('garminFileExpiredLong',
          "Garmin's download window for this file has closed and it cannot be retrieved again. Export it from Garmin Connect and upload it instead."),
      };
    case 'pruned':
      return {
        short: t('garminFilePruned', 'Deleted'),
        long: t('garminFilePrunedLong',
          'This activity was not imported within 90 days, so the file was deleted.'),
      };
    case 'skipped':
      return {
        short: t('garminFileSkipped', 'Not retained'),
        long: t('garminFileSkippedLong', 'The file for this activity was not retained.'),
      };
    default:
      return {
        short: t('garminFileMissing', 'No file'),
        long: t('garminFileMissingLong',
          'Garmin sent the summary for this activity but never sent the file, so there is nothing to analyse.'),
      };
  }
};

const GarminCalendar = ({ sport = 'run', selectedId, onSelect, onConfirm }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [status, setStatus] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);

  const SportIcon = sport === 'bike' ? DirectionsBikeIcon : DirectionsRunIcon;
  const sportColor = sport === 'bike' ? colors.blueAccent[400] : colors.greenAccent[400];

  useEffect(() => {
    getGarminStatus().then(setStatus).catch(() => setStatus(null));
  }, []);

  const load = useCallback(() => {
    if (!status?.connected) { setLoading(false); return; }
    const { start, end } = monthBounds(year, month);
    setLoading(true);
    listGarminActivities(sport, start, end)
      .then((rows) => {
        setActivities(rows);
        setError('');
        // Land on a day that has something on it, rather than an empty list:
        // today if it is in this month and has activities, else the most
        // recent day that does.
        const dates = Array.from(new Set(rows.map((r) => r.local_date).filter(Boolean))).sort();
        const todayIso = iso(new Date());
        setSelectedDate(dates.includes(todayIso) ? todayIso : (dates[dates.length - 1] || null));
      })
      .catch(() => setError(t('garminLoadFailed', 'Could not load your Garmin activities')))
      .finally(() => setLoading(false));
  }, [sport, year, month, status, t]);

  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => {
    const map = {};
    activities.forEach((a) => {
      if (!a.local_date) return;
      (map[a.local_date] = map[a.local_date] || []).push(a);
    });
    Object.values(map).forEach((list) => list.sort(
      (x, y) => new Date(x.start_time) - new Date(y.start_time),
    ));
    return map;
  }, [activities]);

  const weeks = useMemo(() => buildWeeks(year, month), [year, month]);
  const dayActivities = selectedDate ? (byDate[selectedDate] || []) : [];

  const shift = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
    setSelectedDate(null);
  };

  const handleConnect = async () => {
    try {
      window.location.href = await getGarminAuthUrl();
    } catch {
      setError(t('garminConnectFailed', 'Could not start the Garmin connection'));
    }
  };

  // ── not connected ──────────────────────────────────────────────────────────
  if (status && !status.connected) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <WatchIcon sx={{ fontSize: 48, color: colors.grey[500], mb: 1 }} />
        <Typography variant="h6" sx={{ color: colors.grey[100], mb: 1 }}>
          {t('garminConnectTitle', 'Connect Garmin Connect™')}
        </Typography>
        <Typography sx={{ color: colors.grey[300], maxWidth: 460, mx: 'auto', mb: 2 }}>
          {t('garminConnectBlurb',
            'Link your Garmin Connect account and your runs and rides will appear here automatically, ready to analyse.')}
        </Typography>
        {!status.available && (
          <Alert severity="info" sx={{ maxWidth: 460, mx: 'auto', mb: 2 }}>
            {t('garminNotAvailable', 'This integration is not enabled for your account yet.')}
          </Alert>
        )}
        <Button
          variant="contained"
          disabled={!status.available || !status.configured}
          onClick={handleConnect}
          sx={{ textTransform: 'none', backgroundColor: colors.blueAccent[600] }}
        >
          {t('garminConnectCta', 'Connect Garmin Connect™')}
        </Button>
        <Typography variant="body2" sx={{ color: colors.grey[400], mt: 3, maxWidth: 460, mx: 'auto' }}>
          {t('garminHistoryNote',
            'Only activities recorded after you connect will appear here. For an older race, upload the file from your computer.')}
        </Typography>
      </Box>
    );
  }

  const cellBorder = `1px solid ${colors.grey[700]}`;

  return (
    <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
      {/* Header: month nav only. No device name here on purpose — with more
          than one watch a header credit cannot say which activity came from
          which, so the credit lives on each row of the list instead. */}
      <Box display="flex" alignItems="center" gap={0.5} sx={{ px: 1, pb: 1 }}>
        <Button size="small" onClick={() => shift(-1)} sx={{ minWidth: 32, color: colors.grey[100] }}>
          <ChevronLeftIcon />
        </Button>
        <Typography variant="subtitle1" sx={{ color: colors.grey[100], flex: 1, textAlign: 'center', fontWeight: 600 }}>
          {new Date(year, month, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
        </Typography>
        <Button size="small" onClick={() => shift(1)} sx={{ minWidth: 32, color: colors.grey[100] }}>
          <ChevronRightIcon />
        </Button>
        <Button
          size="small"
          onClick={() => {
            const now = new Date();
            setYear(now.getFullYear());
            setMonth(now.getMonth());
            setSelectedDate(iso(now));
          }}
          sx={{ textTransform: 'none', color: colors.grey[300] }}
        >
          {t('today', 'Today')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 1, mb: 1 }}>{error}</Alert>}

      {/* Weekday header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', px: 1 }}>
        {[
          t('mon', 'Mon'), t('tue', 'Tue'), t('wed', 'Wed'), t('thu', 'Thu'),
          t('fri', 'Fri'), t('sat', 'Sat'), t('sun', 'Sun'),
        ].map((d) => (
          <Typography key={d} variant="caption" sx={{ color: colors.grey[400], textAlign: 'center', py: 0.5 }}>
            {d}
          </Typography>
        ))}
      </Box>

      {/* Month grid — markers only, so a busy day never overflows its cell */}
      <Box sx={{ px: 1, position: 'relative', flexShrink: 0 }}>
        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 2, backgroundColor: 'rgba(0,0,0,0.25)',
          }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {weeks.map((days, wi) => (
          <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {days.map((day) => {
              const key = iso(day);
              const inMonth = day.getMonth() === month;
              const isToday = key === iso(today);
              const isSelected = key === selectedDate;
              const items = byDate[key] || [];
              return (
                <Box
                  key={key}
                  onClick={() => items.length && setSelectedDate(key)}
                  sx={{
                    minHeight: 46,
                    border: cellBorder,
                    borderRadius: 0.5,
                    m: 0.25,
                    px: 0.5,
                    py: 0.25,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: items.length ? 'pointer' : 'default',
                    opacity: inMonth ? 1 : 0.35,
                    backgroundColor: isSelected ? colors.blueAccent[800] : 'transparent',
                    outline: isSelected
                      ? `2px solid ${colors.blueAccent[400]}`
                      : (isToday ? `1px solid ${colors.grey[500]}` : 'none'),
                    '&:hover': { backgroundColor: items.length && !isSelected ? colors.primary[600] : undefined },
                  }}
                >
                  <Typography variant="caption" sx={{ color: colors.grey[inMonth ? 300 : 500], lineHeight: 1.4 }}>
                    {day.getDate()}
                  </Typography>
                  <Box display="flex" gap={0.25} alignItems="center" sx={{ mt: 0.25, flexWrap: 'wrap', justifyContent: 'center' }}>
                    {items.slice(0, 3).map((a) => (
                      <Box
                        key={a.id}
                        sx={{
                          width: 6, height: 6, borderRadius: '50%',
                          backgroundColor: a.imported
                            ? colors.greenAccent[500]
                            : (a.importable ? sportColor : colors.grey[500]),
                        }}
                      />
                    ))}
                    {items.length > 3 && (
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: 9, lineHeight: 1 }}>
                        +{items.length - 3}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Selected day's activities */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pt: 1.5, minHeight: 120 }}>
        {selectedDate ? (
          <>
            <Typography variant="subtitle2" sx={{ color: colors.grey[300], mb: 1 }}>
              {new Date(`${selectedDate}T00:00:00`).toLocaleDateString(undefined, {
                weekday: 'long', day: 'numeric', month: 'long',
              })}
            </Typography>
            {dayActivities.map((a) => {
              const isSelected = a.id === selectedId;
              const disabled = !a.importable;
              const reason = disabled ? unavailableReason(a.download_status, t) : null;
              const metrics = [
                fmtDistance(a.distance_m),
                fmtDuration(a.duration_s),
                a.elevation_gain_m != null ? `↑ ${Math.round(a.elevation_gain_m)} m` : null,
                a.avg_heart_rate ? `${Math.round(a.avg_heart_rate)} bpm` : null,
              ].filter(Boolean).join(' · ');

              return (
                <Box
                  key={a.id}
                  onClick={() => !disabled && onSelect?.(a.id)}
                  onDoubleClick={() => !disabled && onConfirm?.(a.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    p: 1.25,
                    mb: 1,
                    borderRadius: 1,
                    borderLeft: `3px solid ${disabled ? colors.grey[600] : sportColor}`,
                    backgroundColor: isSelected ? colors.blueAccent[700] : colors.primary[600],
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    '&:hover': { backgroundColor: disabled ? undefined : colors.blueAccent[800] },
                  }}
                >
                  <SportIcon sx={{ color: disabled ? colors.grey[500] : sportColor, mt: 0.25 }} />
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Typography variant="body2" sx={{ color: colors.grey[100], fontWeight: 600 }} noWrap>
                        {a.name || t('activity', 'Activity')}
                      </Typography>
                      {a.imported && (
                        <CheckCircleIcon sx={{ fontSize: 15, color: colors.greenAccent[400] }} />
                      )}
                    </Box>
                    {metrics && (
                      <Typography variant="caption" sx={{ color: colors.grey[300], display: 'block' }}>
                        {metrics}
                      </Typography>
                    )}
                    {/* Required device credit, per entry. */}
                    <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.25 }}>
                      <WatchIcon sx={{ fontSize: 12, color: colors.grey[400] }} />
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                        {garminDeviceLabel(a.device_name)}
                      </Typography>
                    </Box>
                    {reason && (
                      <Typography variant="caption" sx={{ color: colors.grey[400], display: 'block', mt: 0.5 }}>
                        {reason.long}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flexShrink: 0 }}>
                    {a.imported ? (
                      <Chip size="small" label={t('imported', 'Imported')}
                        sx={{ backgroundColor: colors.greenAccent[700], color: colors.grey[100] }} />
                    ) : disabled ? (
                      <Chip size="small" label={reason.short}
                        sx={{ backgroundColor: colors.grey[700], color: colors.grey[300] }} />
                    ) : (
                      <Chip size="small" label={t('readyToImport', 'Ready')}
                        sx={{ backgroundColor: colors.blueAccent[600], color: colors.grey[100] }} />
                    )}
                  </Box>
                </Box>
              );
            })}
          </>
        ) : (
          <Typography variant="body2" sx={{ color: colors.grey[400], textAlign: 'center', mt: 2 }}>
            {activities.length
              ? t('garminPickADay', 'Pick a day with a marker to see its activities.')
              : t('garminNoActivitiesThisMonth', 'No activities received for this month.')}
          </Typography>
        )}
      </Box>

      {/* The permanent, deliberate line about what this view can and cannot show. */}
      <Typography variant="caption" sx={{ color: colors.grey[400], px: 1.5, py: 1, flexShrink: 0 }}>
        {t('garminHistoryNoteShort',
          'Activities appear from the moment you connected Garmin Connect™; earlier ones cannot be retrieved. Anything you do not import is deleted after 90 days.')}
      </Typography>
    </Box>
  );
};

export default GarminCalendar;
